import {Injectable, OnDestroy} from '@angular/core';
import {KeycloakConfig} from '../models/settings/core.settings';
import {RuntimeContext} from '../models/platform/runtimeContext';
import jwt_decode from 'jwt-decode';
import Keycloak from 'keycloak-js';
import {filter, from, Observable, of, Subject, Subscription, tap, timer} from 'rxjs';
import {switchMap} from 'rxjs/operators';

class Token {
  resource_access: {
    lives: {
      roles: string[];
    };
  };
  preferred_username: string;
  exp: number;
}

export class KeycloakParameter {
  runtimeContext: RuntimeContext = RuntimeContext.STANDALONE;
  keycloakConfig: KeycloakConfig;
  internalRequest: boolean;
}

export interface UserProfile {
  sub: string;
  email: string;
  given_name: string;
  family_name: string;
  token: string;
}


@Injectable()
export class KeycloakLv1871Service implements OnDestroy{
  public profile = {} as UserProfile;
  private initialized = false;
  private _keycloakParameter: KeycloakParameter = new KeycloakParameter();
  private _keycloakInstance: Keycloak;
  private _keycloakInstance$: Subject<Keycloak> = new Subject();
  private _silentRefreshSubscription$ = Subscription.EMPTY;
  private _initResolve: Function;
  private _initPromise = new Promise<boolean>((resolve, _) => {
    this._initResolve = resolve;
  });

  constructor() {
    this._silentRefreshSubscription$ = this._keycloakInstance$.pipe(
        switchMap(() =>
            timer(0, 30000).pipe(
                filter(_ => this.isAuthenticated()),
                filter(_ => this._isRuntimeContextKeycloakRelevant(this._keycloakParameter)),
                switchMap(_ => from(this.tokenUpdaten())),
                tap(resp => {
                  this._initResolve(true);
                  if (resp && !!console) {
                    console.log('########### silent refresh!');
                  }
                })
            )
    )).subscribe();
  }

  ngOnDestroy(): void {
    this._silentRefreshSubscription$?.unsubscribe();
  }

  public setParameter(parameter: KeycloakParameter): void {
    if (!!parameter.runtimeContext) {
      this._keycloakParameter.runtimeContext = parameter.runtimeContext;
    }
    if (!!parameter.keycloakConfig) {
      this._keycloakParameter.keycloakConfig = parameter.keycloakConfig;
    }
    this._keycloakParameter.internalRequest = parameter.internalRequest;
  }

  public getParameter(): KeycloakParameter {
    return this._keycloakParameter;
  }

  public initKeycloak(keycloakParameter?: KeycloakParameter) {
    if (!keycloakParameter) {
      keycloakParameter = this._keycloakParameter;
    } else{
      this._keycloakParameter = keycloakParameter;
    }

    if (!!keycloakParameter.keycloakConfig) {
      this._keycloakInstance = new Keycloak(keycloakParameter.keycloakConfig);
    }

    if (this._isRuntimeContextKeycloakRelevant(keycloakParameter)) {
      this._keycloakInstance.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        silentCheckSsoFallback: false
      }).then(authenticated => {
        if (authenticated){
          this._keycloakInstance$.next(this._keycloakInstance);
        }else{
          this._initResolve(false);
        }
      });
    } else{
      this._initResolve(false);
    }
  }

  public requireKeycloak(keycloakParameter?: KeycloakParameter){
    if (!this.initialized) {
      this.initialized = true;
      this.initKeycloak(keycloakParameter);
    }
    return this._initPromise;
  }

  public hasRole(role: string): boolean {
    let foundRole = false;
    if (!!sessionStorage.getItem('access_token')) {
      const token = jwt_decode(sessionStorage.getItem('access_token')) as Token;
      foundRole = token.resource_access.lives?.roles?.findIndex(tokenRole => tokenRole === role) > -1;
    }
    foundRole = foundRole || !!this._keycloakInstance?.hasResourceRole(role, 'lives');
    return foundRole;
  }

  public isAuthenticated(): boolean {
    return this._keycloakInstance?.authenticated;
  }

  public isAuthenticated$(): Observable<boolean> {
    return of(this._keycloakInstance?.authenticated);
  }

  public getUserName(): string {
    if (!!this._keycloakInstance) {
      if (!!this._keycloakInstance.token) {
        const token = jwt_decode(this._keycloakInstance.token) as Token;
        return token.preferred_username;
      }
    }
    return null;
  }

  public async tokenUpdaten() {
    if (!this._keycloakInstance) {
      throw new Error('Keycloak ist nicht geladen worden.');
    }

    try {
      let expiresIn = 0;
      if (!!sessionStorage.getItem('access_token')) {
        const token = jwt_decode(sessionStorage.getItem('access_token')) as Token;
        expiresIn = token.exp - (new Date().getTime() / 1000);
      }
      let result = false;
      if (!sessionStorage.getItem('access_token') || expiresIn < 300) {
        result = await this._keycloakInstance.updateToken(-1);
        this.profile.token = this._keycloakInstance.token || '';
        sessionStorage.setItem('access_token', this._keycloakInstance.token);
      }
      return Promise.resolve(result);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  private _isRuntimeContextKeycloakRelevant(keycloakParameter: KeycloakParameter) {
    return keycloakParameter.runtimeContext !== RuntimeContext.STANDALONE && keycloakParameter.runtimeContext !== RuntimeContext.CMS;
  }
}
